import { useMemo } from 'react'
import { useSport } from '@/hooks/useSport'
import ServerDataTable from '@/components/ServerDataTable'
import { datatableUrl } from '@/lib/api'
import type { RpiDatatableRow } from '@/types/api'

function buildColumns(sport: string) {
  return [
    {
      data: 'rank',
      title: 'Rank',
      render: (data: number, _type: string, row: RpiDatatableRow) => {
        const prev = row.prev_rank
        if (!prev || prev === data) {
          return `<span class="font-mono font-bold" style="font-variant-numeric:tabular-nums">${data}</span>`
        }
        const delta = prev - data
        const color = delta > 0 ? 'text-trend-up' : 'text-trend-down'
        const arrow = delta > 0 ? '&#9650;' : '&#9660;'
        return `<span class="font-mono font-bold" style="font-variant-numeric:tabular-nums">${data}</span>
                <span class="${color} text-xs ml-1.5">${arrow} ${Math.abs(delta)}</span>`
      },
    },
    {
      data: 'school_name',
      title: 'School',
      render: (data: string, _type: string, row: RpiDatatableRow) => {
        const initials = data.split(' ').map((w: string) => w[0]).join('').slice(0, 3).toUpperCase()
        const logoUrl = row.logo_url
        const avatar = logoUrl
          ? `<img src="${logoUrl}" alt="${data}" class="w-7 h-7 rounded-full object-contain shrink-0 bg-white" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'" /><div class="w-7 h-7 rounded-full bg-accent/20 items-center justify-center text-[10px] font-bold text-accent shrink-0" style="display:none">${initials}</div>`
          : `<div class="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center text-[10px] font-bold text-accent shrink-0">${initials}</div>`
        // Use the persisted team slug; render plain text if the row has no team.
        const label = row.slug
          ? `<a href="/${sport}/teams/${row.slug}" class="hover:text-accent transition-colors">${data}</a>`
          : `<span>${data}</span>`
        return `<div class="flex items-center gap-2.5">
                  ${avatar}
                  ${label}
                </div>`
      },
    },
    { data: 'conference_name', title: 'Conference' },
    { data: 'record_overall', title: 'Overall', className: 'font-mono' },
    { data: 'record_conf', title: 'Conf', className: 'font-mono' },
    { data: 'record_home', title: 'Home', className: 'font-mono' },
    { data: 'record_away', title: 'Away', className: 'font-mono' },
    { data: 'record_neutral', title: 'Neutral', className: 'font-mono' },
    { data: 'prev_rank', title: 'Prev', className: 'font-mono text-slate-500' },
  ]
}

export default function RpiPage() {
  const { sport, sportLabel } = useSport()

  const columns = useMemo(() => buildColumns(sport), [sport])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">{sportLabel} RPI Rankings</h1>
        <p className="text-sm text-slate-500 hidden sm:block">Updated daily from NCAA data</p>
      </div>
      <div className="card overflow-hidden">
        <ServerDataTable
          key={sport}
          ajaxUrl={datatableUrl(sport, 'rpi')}
          columns={columns}
          order={[[0, 'asc']]}
          pageLength={50}
        />
      </div>
    </div>
  )
}
